/**
 * <faq-accordion>: animated open/close for the FAQ block's rows (<details class="faq-row">).
 *
 * - Optionally closes the other rows when one opens (data-close-others).
 * - Opens the row whose id matches the URL hash.
 */
(() => {
  if (customElements.get('faq-accordion')) return;

  class FaqAccordion extends HTMLElement {
    #abort = new AbortController();

    connectedCallback() {
      const { signal } = this.#abort;

      this.addEventListener('click', this.#handleClick, { signal });
      window.addEventListener('hashchange', this.#openFromHash, { signal });

      this.#openFromHash();
    }

    disconnectedCallback() {
      this.#abort.abort();
    }

    get #rows() {
      return Array.from(this.querySelectorAll('.faq-row'));
    }

    get #duration() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
      return Number(this.dataset.duration) || 0;
    }

    /** @param {MouseEvent} event */
    #handleClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const summary = event.target.closest('.faq-row__summary');
      if (!summary || !this.contains(summary)) return;

      const row = summary.closest('.faq-row');
      if (!(row instanceof HTMLDetailsElement)) return;

      // Take over from the browser's instant toggle so the panel can animate
      event.preventDefault();

      if (row.open) {
        this.#close(row);
      } else {
        if (this.dataset.closeOthers === 'true') {
          for (const other of this.#rows) if (other !== row && other.open) this.#close(other);
        }
        this.#open(row);
      }
    };

    #openFromHash = () => {
      const id = decodeURIComponent(window.location.hash.slice(1));
      if (!id) return;

      const row = this.#rows.find((candidate) => candidate.id === id);
      if (row instanceof HTMLDetailsElement && !row.open) {
        row.open = true;
        row.scrollIntoView({ block: 'nearest' });
      }
    };

    /** @param {HTMLDetailsElement} row */
    #open(row) {
      const panel = row.querySelector('.faq-row__panel');
      row.open = true;
      if (!(panel instanceof HTMLElement)) return;

      this.#animate(panel, [0, panel.scrollHeight], () => {});
    }

    /** @param {HTMLDetailsElement} row */
    #close(row) {
      const panel = row.querySelector('.faq-row__panel');
      if (!(panel instanceof HTMLElement)) {
        row.open = false;
        return;
      }

      this.#animate(panel, [panel.offsetHeight, 0], () => {
        row.open = false;
      });
    }

    /**
     * @param {HTMLElement} panel
     * @param {[number, number]} heights
     * @param {() => void} done
     */
    #animate(panel, [from, to], done) {
      panel.getAnimations().forEach((animation) => animation.cancel());

      const duration = this.#duration;
      if (duration === 0) {
        done();
        return;
      }

      const animation = panel.animate(
        { height: [`${from}px`, `${to}px`] },
        { duration, easing: this.dataset.easing || 'ease' }
      );
      animation.onfinish = done;
    }
  }

  customElements.define('faq-accordion', FaqAccordion);
})();
